import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  Check,
  Minus,
  Sparkles,
  MessageCircle,
  Phone,
  PhoneOutgoing,
  CalendarCheck,
  Bot,
  ShieldCheck,
  BarChart3,
  Loader2,
} from 'lucide-react'
import { ConvertDemoDialog, type DemoTierSlug } from '@/components/billing/ConvertDemoDialog'
import { ChangePlanModal } from '@/components/billing/ChangePlanModal'

type TierSlug = 'associate' | 'partner' | 'principal'

interface SubscriptionState {
  tier: string
  status: string
  stripeCustomerId?: string
  stripeSubscriptionId?: string
}

interface TierCopy {
  slug: TierSlug
  name: string
  monthly: number
  promise: string
  description: string
  highlights: string[]
  mostPopular?: boolean
}

const TIERS: TierCopy[] = [
  {
    slug: 'associate',
    name: 'Associate',
    monthly: 397,
    promise: 'Your first AI intake layer — every website visitor gets a real conversation.',
    description:
      "Your firm's own AI receptionist, trained on your practice areas, your website, and a knowledge base you control. Every visitor gets a tailored conversation; every qualified lead lands on your calendar. Your team sees the conversation in real time and can take over with one click.",
    highlights: [
      'AI chat trained on your firm, website, and knowledge base',
      'Bookings on your calendar with live staff takeover',
      'Team management, chat history, custom alerts',
      'Practice-area analytics for the whole firm',
    ],
  },
  {
    slug: 'partner',
    name: 'Partner',
    monthly: 897,
    mostPopular: true,
    promise: 'Intake that never sleeps — web and phone, 24/7.',
    description:
      'Everything in Associate, plus your AI answers every inbound call, day or night. No voicemail, no missed prospects, no after-hours gap. Calls are transcribed, summarized, and scored the moment they end — your team walks in each morning knowing exactly who called and what they need.',
    highlights: [
      'Everything in Associate',
      'AI answers every call instantly, 24/7',
      'Dedicated phone number, 250 calls/mo included',
      'Transcripts, summaries, and caller history for every call',
    ],
  },
  {
    slug: 'principal',
    name: 'Principal',
    monthly: 1497,
    promise: 'Every inquiry becomes a conversation — proactive callback + CRM sync.',
    description:
      "Everything in Partner, plus proactive outreach. The moment a prospect submits an inquiry, your AI calls them personally — turning form submissions into live conversations while the lead is still warm. If a caller hangs up mid-intake, your AI follows up to finish the matter. Paired with CRM sync, reminder calls, and priority support, nothing in your funnel goes unattended.",
    highlights: [
      'Everything in Partner',
      'Proactive callback on every web inquiry',
      'CRM sync (Clio, Lawmatics, HubSpot, and more)',
      '600 calls/mo, reminder calls, priority support',
    ],
  },
]

const COMPARISON: Array<{
  feature: string
  associate: boolean | string
  partner: boolean | string
  principal: boolean | string
}> = [
  { feature: 'AI Chat Agent (knows your firm)', associate: true, partner: true, principal: true },
  { feature: 'AI Inbound Caller (answers your phone)', associate: false, partner: true, principal: true },
  { feature: 'AI Outbound Caller (calls leads back)', associate: false, partner: false, principal: true },
  { feature: 'Phone number', associate: '—', partner: '1 local', principal: '1 local' },
  { feature: 'Calls included', associate: '—', partner: '250/mo', principal: '600/mo' },
  { feature: 'Chat messages', associate: '5,000', partner: 'Unlimited', principal: 'Unlimited' },
  { feature: 'Knowledge base + practice areas', associate: true, partner: true, principal: true },
  { feature: 'Bookings + calendar', associate: true, partner: true, principal: true },
  { feature: 'Lead scoring', associate: true, partner: true, principal: true },
  { feature: 'Live human takeover (chat)', associate: true, partner: true, principal: true },
  { feature: 'Call transcripts + summaries', associate: false, partner: true, principal: true },
  { feature: 'Caller history', associate: false, partner: true, principal: true },
  { feature: 'Auto callback on web inquiries', associate: false, partner: false, principal: true },
  { feature: 'Auto follow-up on dropped calls', associate: false, partner: false, principal: true },
  { feature: 'Reminder calls before appointments', associate: false, partner: false, principal: true },
  { feature: 'CRM integrations', associate: false, partner: false, principal: true },
  { feature: 'Priority support', associate: false, partner: false, principal: true },
]

const ADDONS = [
  { name: 'Inbound Agent', price: 199, description: 'Add inbound to an existing number' },
  { name: 'Outbound Agent', price: 199, description: 'Add outbound to an existing number' },
  { name: 'Agent Pair + Number', price: 349, description: '1 number + both agents (save $74)' },
  { name: 'Local Number', price: 25, description: 'Additional local phone number' },
  { name: 'Toll-Free Number', price: 35, description: 'Additional toll-free number' },
  { name: 'Premium Voice', price: 99, description: 'Upgraded ElevenLabs voice' },
]

const CALL_PACKS = [
  { calls: 10, price: 20 },
  { calls: 25, price: 45 },
  { calls: 50, price: 80 },
  { calls: 100, price: 150 },
  { calls: 250, price: 350 },
  { calls: 500, price: 650 },
]

function tierAccent(slug: TierSlug) {
  if (slug === 'associate') return { ring: 'border-sky-400/40', dot: 'bg-sky-400', text: 'text-sky-600 dark:text-sky-400', check: 'text-sky-500' }
  if (slug === 'partner') return { ring: 'border-brand-periwinkle/50', dot: 'bg-brand-periwinkle', text: 'text-brand-royal dark:text-brand-periwinkle', check: 'text-brand-periwinkle' }
  return { ring: 'border-violet-400/40', dot: 'bg-violet-400', text: 'text-violet-600 dark:text-violet-400', check: 'text-violet-500' }
}

function renderCell(value: boolean | string, accentText?: string) {
  if (value === true) return <Check className={`h-4 w-4 mx-auto ${accentText ?? 'text-brand-periwinkle'}`} strokeWidth={2.5} />
  if (value === false) return <Minus className="h-4 w-4 mx-auto text-muted-foreground/40" strokeWidth={2} />
  return <span className="text-sm text-foreground/80 tabular-nums">{value}</span>
}

export default function BillingPlansPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [sub, setSub] = useState<SubscriptionState | null>(null)
  const [loading, setLoading] = useState(true)
  const [convertOpen, setConvertOpen] = useState(false)
  const [convertTier, setConvertTier] = useState<DemoTierSlug>('partner')
  const [changePlanOpen, setChangePlanOpen] = useState(false)
  const [changePlanInitialTier, setChangePlanInitialTier] = useState<TierSlug | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    api.get('/api/billing/subscription')
      .then((res) => { if (!cancelled) setSub(res.data?.data ?? null) })
      .catch(() => { if (!cancelled) setSub(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Anyone without a live Stripe subscription needs to go through the
  // subscribe flow (creates customer + sub + attaches card in one shot).
  // The change-plan flow can't help these users — its proration preview
  // requires an existing subscription on Stripe's side.
  const needsSubscribe =
    !!sub && (sub.status === 'demo_active' || !sub.stripeCustomerId || !sub.stripeSubscriptionId)
  const currentTier = (sub?.tier as TierSlug | undefined) ?? null

  const handlePick = (slug: TierSlug) => {
    if (needsSubscribe) {
      setConvertTier(slug)
      setConvertOpen(true)
    } else {
      // Paid with a live sub — preselect the clicked tier in ChangePlanModal
      // so the user sees proration straight away without picking twice.
      setChangePlanInitialTier(slug)
      setChangePlanOpen(true)
    }
  }

  const handleSubscribed = () => {
    setConvertOpen(false)
    toast({
      title: 'Welcome aboard',
      description: 'Your subscription is active — your demo has been converted.',
    })
    navigate('/settings/billing')
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate('/settings/billing')}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to billing
          </button>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">
            Choose your plan
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Three tiers designed around how your firm wants to handle intake. Every plan includes the AI chat that knows your firm; higher tiers add the phone line and proactive outreach.
          </p>
        </div>
        {!loading && (
          <div className="text-xs text-muted-foreground self-start sm:self-auto">
            {needsSubscribe ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-periwinkle/30 bg-brand-periwinkle/5 px-2.5 py-1 text-brand-royal dark:text-brand-periwinkle font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-periwinkle animate-pulse shrink-0" />
                {sub?.status === 'demo_active' ? 'Demo · pick a plan to continue' : 'Pick a plan to subscribe'}
              </span>
            ) : currentTier ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-500/30 bg-green-500/5 px-2.5 py-1 text-green-700 dark:text-green-400 font-medium">
                <Check className="h-3 w-3 shrink-0" />
                Current: {TIERS.find(t => t.slug === currentTier)?.name ?? currentTier}
              </span>
            ) : null}
          </div>
        )}
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
        {TIERS.map((tier) => {
          const accent = tierAccent(tier.slug)
          const isCurrent = currentTier === tier.slug
          const buttonLabel = needsSubscribe
            ? `Subscribe to ${tier.name}`
            : isCurrent
              ? 'Current plan'
              : 'Switch plan'
          return (
            <div
              key={tier.slug}
              className={`relative rounded-2xl border bg-card shadow-sm card-inset overflow-hidden ${
                tier.mostPopular ? 'lg:-translate-y-1 ring-1 ring-brand-periwinkle/30' : ''
              }`}
            >
              {tier.mostPopular && (
                <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider shadow-sm">
                  <Sparkles className="h-3 w-3" />
                  Most popular
                </span>
              )}

              <div className="p-6 sm:p-7">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${accent.dot}`} aria-hidden="true" />
                  <span className={`text-[11px] font-bold uppercase tracking-[0.18em] ${accent.text}`}>{tier.name}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-3 min-h-[40px] leading-relaxed">{tier.promise}</p>

                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-4xl font-heading font-bold tabular-nums">${tier.monthly}</span>
                  <span className="text-sm text-muted-foreground">/mo</span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">Billed monthly. Cancel anytime.</p>

                <ul className="space-y-2.5 mt-6 min-h-[140px]">
                  {tier.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-sm text-foreground/85">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${accent.check}`} strokeWidth={2.5} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handlePick(tier.slug)}
                  disabled={loading || isCurrent}
                  className={`w-full mt-6 h-11 ${
                    tier.mostPopular
                      ? 'bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 shadow-md shadow-brand-periwinkle/20'
                      : ''
                  }`}
                  variant={tier.mostPopular ? 'default' : 'outline'}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
                </Button>
              </div>
            </div>
          )
        })}
      </div>

      {/* What each plan really does — long-form copy */}
      <div className="rounded-2xl border bg-card shadow-sm card-inset overflow-hidden">
        <div className="p-6 sm:p-7">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-5">
            What each plan really does
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {TIERS.map((tier) => {
              const accent = tierAccent(tier.slug)
              return (
                <div key={tier.slug} className="space-y-2.5">
                  <h3 className={`text-sm font-semibold pb-2 border-b ${accent.text}`} style={{ borderBottomColor: 'rgb(from currentColor r g b / 0.25)' }}>
                    {tier.name}
                  </h3>
                  <p className="text-sm text-foreground/75 leading-relaxed">{tier.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Feature comparison */}
      <div className="rounded-2xl border bg-card shadow-sm card-inset overflow-hidden">
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-2 mb-5">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Feature comparison</h2>
          </div>
          <p className="text-[11px] text-muted-foreground mb-2 sm:hidden">
            Swipe sideways to see all three plans →
          </p>
          <div className="rounded-xl border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm border-collapse">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="sticky left-0 z-10 bg-muted/40 text-left px-3 sm:px-5 py-3 font-medium text-muted-foreground text-[11px] uppercase tracking-wider">Feature</th>
                    <th className="px-3 sm:px-5 py-3 font-medium text-muted-foreground text-[11px] uppercase tracking-wider">Associate</th>
                    <th className="px-3 sm:px-5 py-3 font-medium text-brand-royal dark:text-brand-periwinkle text-[11px] uppercase tracking-wider">Partner</th>
                    <th className="px-3 sm:px-5 py-3 font-medium text-muted-foreground text-[11px] uppercase tracking-wider">Principal</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, idx) => {
                    const rowBg = idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                    return (
                      <tr key={row.feature} className={rowBg}>
                        <td className={`sticky left-0 z-10 ${rowBg} px-3 sm:px-5 py-3 text-foreground/85`}>{row.feature}</td>
                        <td className="px-3 sm:px-5 py-3 text-center">{renderCell(row.associate, 'text-sky-500')}</td>
                        <td className="px-3 sm:px-5 py-3 text-center bg-brand-periwinkle/[0.04]">{renderCell(row.partner, 'text-brand-periwinkle')}</td>
                        <td className="px-3 sm:px-5 py-3 text-center">{renderCell(row.principal, 'text-violet-500')}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add-ons + Call packs (informational) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
        <div className="rounded-2xl border bg-card shadow-sm card-inset overflow-hidden">
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-1">
              <PhoneOutgoing className="h-4 w-4 text-brand-periwinkle" />
              <h3 className="text-base font-semibold">Add-ons</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Mix and match to extend any plan. Available after you subscribe.
            </p>
            <div className="rounded-xl border overflow-hidden">
              {ADDONS.map((addon, idx) => (
                <div
                  key={addon.name}
                  className={`flex items-start justify-between gap-3 px-4 sm:px-5 py-3 ${
                    idx < ADDONS.length - 1 ? 'border-b' : ''
                  } ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{addon.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{addon.description}</p>
                  </div>
                  <span className="text-sm font-semibold text-brand-royal dark:text-brand-periwinkle tabular-nums shrink-0 whitespace-nowrap pt-0.5">
                    ${addon.price}<span className="text-[10px] font-normal text-muted-foreground">/mo</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border bg-card shadow-sm card-inset overflow-hidden">
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-2 mb-1">
              <Phone className="h-4 w-4 text-brand-periwinkle" />
              <h3 className="text-base font-semibold">Call packs</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Need more calls in a busy month? Buy a pack — they roll into your bucket instantly.
            </p>
            <div className="rounded-xl border overflow-hidden">
              {CALL_PACKS.map((pack, idx) => (
                <div
                  key={pack.calls}
                  className={`flex items-center justify-between px-4 sm:px-5 py-3 ${
                    idx < CALL_PACKS.length - 1 ? 'border-b' : ''
                  } ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}`}
                >
                  <span className="text-sm font-medium">{pack.calls} calls</span>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-brand-royal dark:text-brand-periwinkle tabular-nums">${pack.price}</span>
                    <span className="text-[11px] text-muted-foreground ml-2 tabular-nums">
                      ${(pack.price / pack.calls).toFixed(2)}/call
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* What's always included — reassurance row */}
      <div className="rounded-2xl border bg-card shadow-sm card-inset overflow-hidden">
        <div className="p-6 sm:p-7">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-5">
            Every plan includes
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReassureItem
              icon={<Bot className="h-4 w-4" />}
              title="Trained on your firm"
              body="Every conversation reflects your practice areas, your tone, and your knowledge base."
            />
            <ReassureItem
              icon={<MessageCircle className="h-4 w-4" />}
              title="Human takeover"
              body="Jump into any chat the moment you want to. The AI hands the conversation off cleanly."
            />
            <ReassureItem
              icon={<CalendarCheck className="h-4 w-4" />}
              title="Bookings on your calendar"
              body="Qualified leads land directly on your calendar with the matter type already captured."
            />
            <ReassureItem
              icon={<ShieldCheck className="h-4 w-4" />}
              title="No long-term contract"
              body="Month-to-month. Cancel anytime — your data stays yours either way."
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <ConvertDemoDialog
        open={convertOpen}
        onOpenChange={setConvertOpen}
        tier={convertTier}
        onSuccess={handleSubscribed}
      />

      {!needsSubscribe && currentTier && (
        <ChangePlanModal
          open={changePlanOpen}
          onOpenChange={setChangePlanOpen}
          currentTier={currentTier}
          currentInterval="monthly"
          initialSelectedTier={changePlanInitialTier}
          onPlanChanged={() => {
            setChangePlanOpen(false)
            toast({ title: 'Plan updated', description: 'Your new plan is now active.' })
            navigate('/settings/billing')
          }}
        />
      )}
    </div>
  )
}

interface ReassureItemProps {
  icon: React.ReactNode
  title: string
  body: string
}

function ReassureItem({ icon, title, body }: ReassureItemProps) {
  return (
    <div className="flex flex-col">
      <div className="h-8 w-8 rounded-lg bg-brand-periwinkle/10 text-brand-royal dark:text-brand-periwinkle flex items-center justify-center mb-3">
        {icon}
      </div>
      <p className="text-sm font-semibold mb-1">{title}</p>
      <p className="text-xs text-muted-foreground leading-relaxed">{body}</p>
    </div>
  )
}
